// E14-t3 — THE KNOWLEDGE-BASE REGISTRY, as the browser may ask for it.
//
// Contract: vault/Apps/Forge/Pages/Home.md — "A KNOWLEDGE BASE IS SHOWN WITH ITS
//   DATA, not just its name. `GET /api/kbs` returns { id, org, name,
//   defaultBranch, gitUrl, webUrl, verdict } per KB, and the verdict is the
//   point." f5 adds the write: creating one from here, against POST /api/kbs.
// Contract: vault/Backlog/E14 Customer Facing UI.md q2 (answered, "Both on
//   Home") — the storage/file counts come from GET /api/:org/:kb/usage, ONE
//   request per KB. That cost is the accepted price of the answer, which is why
//   `loadUsage` is a separate method the page calls per KB rather than something
//   folded into the list: a KB whose usage read fails must degrade to "unknown"
//   on its own card and not take the list down with it.
// Contract: vault/Flows/KB Creation and Import.md f1 — the D1 registry row is
//   provisioned by POST /api/kbs and nowhere else, and s1: what the person needs
//   next is the CLONE URL, said by the host. Both are read out of the answer here.
//
// SHAPES ARE THE CONTROL PLANE'S, READ FROM git-host, NEVER INVENTED:
//   git-host/src/auth.ts::listKbs   builds each row, and `verdict` is whatever
//     `branchState` found on the DEFAULT branch — `null` for a KB nobody has
//     pushed to, and `null` again when the KB's Durable Object cannot be reached
//     (that function swallows the failure deliberately: "a KB whose object cannot
//     be reached is still a KB, listed without a verdict"). So null means "no
//     answer", which is NOT the same as "nothing is wrong", and the page has to
//     say the difference.
//   git-host/src/verdict.ts::Verdict is the object inside it: oid, status,
//     format, primaryFormat, errors, warnings, findings, gates, checkedAt, at.
//     THE FINDINGS ARE DELIBERATELY DROPPED HERE. Git Host f3 stores the FULL
//     findings and this is a landing page: a list of KBs is not where a hundred
//     findings belong, and parsing them into a type nobody renders would be a
//     promise this module cannot keep. The counts are what the card shows.
//   git-host/src/quota.ts::usageSurface answers the usage route with { orgId,
//     kbId, usage: { bytes, files }, limits, files: [...] }. Only `usage` is read
//     below — the per-file ledger belongs to the Admin page (Admin.md f3), which
//     already asks for the same route through src/api/admin.ts and reads all of it.
//
// Shaped like src/api/admin.ts and src/api/dashboards.ts, including the
// late-bound default fetch (a thunk, so this module is safe to evaluate at
// import time in an environment that has no `fetch` until later).
import { appFetch } from './transport';
import type { GateStatus } from '../../../platform/src/guards';

/** git-host/src/verdict.ts — the referee's answer for ONE branch, minus the
 *  findings this surface does not show. */
export interface KbVerdict {
  /** platform/src/guards.ts GateStatus: green IFF the primary gate produced no
   *  error-level finding. `unknown` is the referee's own third answer and is not
   *  the same as no verdict at all — see `KbRow.verdict`. */
  status: GateStatus;
  /** the primary format's name, as the KB names it */
  format: string | null;
  errors: number;
  warnings: number;
  /** the commit the verdict was computed over — a verdict always names its tip */
  oid: string | null;
  /** ISO-8601; `checkedAt`, or the `at` alias Git Host f3 uses for it */
  checkedAt: string | null;
}

/** One row of GET /api/kbs. Every field is the host's own; nothing is assembled. */
export interface KbRow {
  id: string;
  /** the ORG NAME, which is also the :org url segment */
  org: string;
  name: string;
  defaultBranch: string;
  /** said by the host (djc/src/registry.js), never built from window.location */
  gitUrl: string | null;
  webUrl: string | null;
  /** null when nothing has been refereed on the default branch — or when the
   *  KB's Durable Object did not answer. The list cannot tell those apart, and
   *  neither may the page pretend to. */
  verdict: KbVerdict | null;
}

/** platform/src/accounting.ts KbUsage — the two totals E14 q2 asked for.
 *  `limits` (Billing f2) is the same answer's own caps — git-host/src/quota.ts
 *  usageSurface sends { limits: { kbBytes, kbFiles } } (the platform beta caps)
 *  beside the totals, and the Billing page draws its reference line from the
 *  ANSWER rather than importing a constant the Worker might stop enforcing.
 *  Optional because older tests and callers built the two-field shape; absent
 *  limits mean "the server did not say", and no line is drawn from a guess. */
export interface KbUsage {
  bytes: number;
  files: number;
  limits?: { kbBytes: number; kbFiles: number };
}

/** POST /api/kbs — the whole body the route reads (git-host/src/auth.ts createKb). */
export interface CreateKbInput {
  name: string;
  /** the org NAME. Sent explicitly: omitting it lets the Worker fall back to the
   *  caller's default org, and a page that shows ONE organization must create in
   *  the organization it is showing rather than in whichever one D1 sorts first. */
  org: string;
}

/** What 201 answers with — including the clone url the person needs next. */
export interface CreatedKb {
  org: string;
  name: string;
  defaultBranch: string;
  gitUrl: string | null;
  webUrl: string | null;
}

/** git-host/src/events.ts PushActivityDay — one day a KB was pushed to. Days
 *  with no pushes are ABSENT (the wire carries no zeros); the consumer
 *  zero-fills its window. */
export interface ActivityDay {
  /** UTC date, `YYYY-MM-DD` — the event's own `at`, first ten characters */
  day: string;
  accepted: number;
  refused: number;
}

/** The questions this app asks about knowledge bases. Injected, so no page
 *  holds a fetch of its own. */
export interface KbsClient {
  /** every KB this session can reach, across ALL its orgs — the route is scoped
   *  to the caller's memberships and to nothing narrower (Tenancy f5), so the
   *  per-org filter is the page's, not the server's */
  listKbs(): Promise<KbRow[]>;
  loadUsage(org: string, kb: string): Promise<KbUsage>;
  /** E21-t3 — GET /api/:org/:kb/activity: push history by day, derived from the
   *  ops_events witness at the write gate. Same per-KB degrade contract as
   *  loadUsage: one KB's failure is that card's alone. */
  loadActivity(org: string, kb: string): Promise<ActivityDay[]>;
  createKb(input: CreateKbInput): Promise<CreatedKb>;
}

export const KBS_ENDPOINT = '/api/kbs';

/** git-host/src/index.ts — answered at the Worker beside the membership guard
 *  (its truth is D1's ops_events, not the repository's), addressed WITHOUT
 *  `.git` like the rest of /api/:org/:kb/*. */
export const activityEndpoint = (org: string, kb: string): string =>
  `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/activity`;

/** git-host/src/quota.ts — the accounting ledger's own surface, addressed
 *  WITHOUT `.git` like the rest of /api/:org/:kb/*. */
export const usageEndpoint = (org: string, kb: string): string =>
  `/api/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/usage`;

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const STATUSES: readonly GateStatus[] = ['green', 'red', 'unknown'];

/**
 * A stored verdict, or null.
 *
 * A status word this app does not know becomes `unknown` rather than being shown
 * raw: the three words are platform/src/guards.ts's whole vocabulary, and a
 * fourth one on a card would be a colour nobody can read.
 */
export function asVerdict(value: unknown): KbVerdict | null {
  if (value === null || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const status = raw.status as GateStatus;
  return {
    status: STATUSES.includes(status) ? status : 'unknown',
    format: str(raw.format) ?? str(raw.primaryFormat),
    errors: num(raw.errors),
    warnings: num(raw.warnings),
    oid: str(raw.oid),
    checkedAt: str(raw.checkedAt) ?? str(raw.at),
  };
}

/**
 * A refusal is an ordinary answer, and the server's own words are the ones that
 * travel. `message` is what git-host/src/auth.ts sends for a KB-name refusal and
 * for the 409 a taken name earns ("acme already has a knowledge base called
 * …"); `reason` plus `guard` is what a guard refusal sends (src/serve.ts's
 * shape, relayed the same way src/api/admin.ts relays it). Inventing a sentence
 * here would hide which of those two happened.
 */
async function refusalOf(response: Response, what: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as {
    guard?: string;
    reason?: string;
    message?: string;
    error?: string;
  };
  const said = body.message ?? body.reason ?? body.error;
  return new Error(
    `${body.guard ? `${body.guard}: ` : ''}${said ?? `${what} answered ${response.status}`}`,
  );
}

export function createKbsClient(
  fetchImpl: typeof fetch = (input, init) => appFetch(input, init),
): KbsClient {
  return {
    async listKbs(): Promise<KbRow[]> {
      // credentials 'same-origin': the members-only session travels as the
      // `dj_session` cookie (platform/src/session-store.ts) and the app and the
      // Worker are ONE origin in the browser (web/vite.config.ts proxies /api).
      const response = await fetchImpl(KBS_ENDPOINT, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${KBS_ENDPOINT}`);
      const body = (await response.json()) as { kbs?: Array<Record<string, unknown>> };
      const rows: KbRow[] = [];
      for (const entry of body.kbs ?? []) {
        const name = str(entry?.name);
        const org = str(entry?.org);
        // A row that cannot be addressed is not a row: /:org/:kb is how every
        // other request about it is written, so one without both is dropped
        // rather than rendered as a card that leads nowhere.
        if (name === null || org === null) continue;
        rows.push({
          id: str(entry.id) ?? `${org}/${name}`,
          org,
          name,
          defaultBranch: str(entry.defaultBranch) ?? 'main',
          gitUrl: str(entry.gitUrl),
          webUrl: str(entry.webUrl),
          verdict: asVerdict(entry.verdict ?? null),
        });
      }
      return rows;
    },

    async loadUsage(org: string, kb: string): Promise<KbUsage> {
      const path = usageEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${path}`);
      const body = (await response.json()) as {
        usage?: Record<string, unknown>;
        limits?: Record<string, unknown>;
      };
      const kbBytes = num(body.limits?.kbBytes);
      const kbFiles = num(body.limits?.kbFiles);
      return {
        bytes: num(body.usage?.bytes),
        files: num(body.usage?.files),
        // A zero cap is no cap anybody set — num() collapses absent and
        // malformed to 0, and a reference line at 0 would read as "everything
        // is over", so the pair travels only when both are real.
        ...(kbBytes > 0 && kbFiles > 0 ? { limits: { kbBytes, kbFiles } } : {}),
      };
    },

    async loadActivity(org: string, kb: string): Promise<ActivityDay[]> {
      const path = activityEndpoint(org, kb);
      const response = await fetchImpl(path, { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, `GET ${path}`);
      const body = (await response.json()) as { days?: Array<Record<string, unknown>> };
      const days: ActivityDay[] = [];
      for (const entry of body.days ?? []) {
        const day = str(entry?.day);
        // A row with no date cannot be placed on any axis — dropped, like the
        // unaddressable KB row above, rather than plotted at a guess.
        if (day === null) continue;
        days.push({ day, accepted: num(entry.accepted), refused: num(entry.refused) });
      }
      return days;
    },

    async createKb(input: CreateKbInput): Promise<CreatedKb> {
      const response = await fetchImpl(KBS_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: input.name, org: input.org }),
      });
      // 409 is the one refusal a person will actually hit — UNIQUE (org_id, name)
      // in platform/migrations/0001 — and it arrives with the sentence to show.
      if (!response.ok) throw await refusalOf(response, `POST ${KBS_ENDPOINT}`);
      const body = (await response.json()) as Record<string, unknown>;
      return {
        org: str(body.org) ?? input.org,
        name: str(body.name) ?? input.name,
        defaultBranch: str(body.defaultBranch) ?? 'main',
        gitUrl: str(body.gitUrl),
        webUrl: str(body.webUrl),
      };
    },
  };
}
